import { Trans } from 'react-i18next'

import translationKeys from '@translationKeys'

import LinearProgressWithLabel from '@components/LinearProgressWithLabel'
import {
  AnyProfile,
  Company,
  CompanyAsset,
  HistoryProfileEvent,
  MetaFilterTypes,
  ProfileType,
  SearchFilterByRecent,
} from '@modules/search/types'
import { Typography } from '@mui/material'
import Link from '@mui/material/Link'
import { calcStageProgress } from '@shared/utils'

import { ResultProfileItemProps } from './components/ResultProfileItem'

const formatTextList = (v: string[] | string) => (Array.isArray(v) ? v.join(', ') : '')

const ASSETS_ORDER = [ProfileType.biotech, ProfileType.medtech, ProfileType.services, ProfileType.technologies]

export const ASSETS_SHORT_NAMES: Record<string, React.ReactNode> = {
  [ProfileType.biotech]: <Trans>{translationKeys.ASSET_BIOTECH_SHORT_NAME}</Trans>,
  [ProfileType.medtech]: <Trans>{translationKeys.ASSET_MEDTECH_SHORT_NAME}</Trans>,
  [ProfileType.services]: <Trans>{translationKeys.ASSET_SERVICES_SHORT_NAME}</Trans>,
  [ProfileType.technologies]: <Trans>{translationKeys.ASSET_TECHNOLOGIES_SHORT_NAME}</Trans>,
}

export const BIO_DEV_PHASE_ITEMS = [
  'N/A',
  'Discovery',
  'Lead optimization',
  'Preclinical',
  'Phase I',
  'Phase II',
  'Phase III',
  'NDA',
  'On the market',
]

export const MED_DEV_PHASE_ITEMS = ['N/A', 'Development', 'On the market']

export const COMPANY_PREVIEW_FIELDS = [
  {
    field: 'description',
    label: '',
  },
  {
    field: 'primaryTherapeuticAreas',
    label: 'Primary therapeutic area(s)',
    format: formatTextList,
  },
  {
    field: 'sector',
    label: 'Sector',
  },
  {
    field: 'subSectors',
    label: 'Subsector(s)',
    format: formatTextList,
  },
  {
    field: 'location',
    label: 'Location',
    render: (profile: AnyProfile) => {
      const { country, city, addressLine1, addressLine2, state, zip } = profile as Company
      let fullAddress = ''

      if (addressLine1) {
        fullAddress += `${addressLine1}`
      }

      if (addressLine2) {
        fullAddress += !fullAddress.length ? addressLine2 : `, ${addressLine2}`
      }

      if (zip) {
        fullAddress += !fullAddress.length ? zip : `, ${zip}`
      }

      if (city) {
        fullAddress += !fullAddress.length ? city : `, ${city}`
      }

      if (country) {
        fullAddress += !fullAddress.length ? country : `, ${country}`
      }

      if (state) {
        fullAddress += !fullAddress.length ? state : `, ${state}`
      }

      if (!fullAddress) {
        return null
      }

      return <Typography>{fullAddress}</Typography>
    },
  },

  {
    field: 'counters',
    label: 'Current Assets',
    render: (profile: AnyProfile, events?: HistoryProfileEvent[]) => {
      const { counters, hasAssets } = profile as Company

      if (!hasAssets) {
        return null
      }

      const assetsContent =
        counters &&
        Object.entries(counters)
          .filter(([, count]) => count)
          .sort(([a], [b]) => ASSETS_ORDER.indexOf(a as ProfileType) - ASSETS_ORDER.indexOf(b as ProfileType))
          .map(([assetName, assetsNumber], index, arr) => (
            <>
              {assetsNumber} {ASSETS_SHORT_NAMES[assetName]}
              {index < arr.length - 1 && ', '}
            </>
          ))

      return (
        <>
          {assetsContent}
          {events && events.length > 1 && (
            <div>
              <Link href={`/plus/profile/${profile.id}/products`} underline="none">
                View assets
              </Link>
            </div>
          )}
        </>
      )
    },
  },
  {
    field: 'website',
    label: 'Website',
  },
]

export const DELEGATE_PREVIEW_FIELDS = [
  {
    field: 'jobTitle',
    label: 'Job title',
  },
  {
    field: 'profileSummary',
    label: 'Background',
  },
  {
    field: 'companyName',
    label: 'Company',
  },
]

export const MEDTECH_PREVIEW_FIELDS = [
  {
    field: 'description',
    label: '',
  },
  {
    field: 'marketingRightAvailableSought',
    label: 'Marketing rights available / sought',
    format: formatTextList,
  },
  {
    field: 'companyName',
    label: 'Company name',
  },
  {
    field: 'devPhase',
    label: 'Development phase',
    render: (profile: AnyProfile) => {
      const { devPhase } = profile as CompanyAsset
      const value = devPhase || 'N/A'

      return <LinearProgressWithLabel label={value} value={calcStageProgress(MED_DEV_PHASE_ITEMS, value)} />
    },
  },
  {
    field: 'differentiator',
    label: 'Partnering status',
  },
  {
    field: 'categorySector',
    label: 'Sector',
  },
  {
    field: 'categorySubSectors',
    label: 'Subsector(s)',
    format: formatTextList,
  },
]

export const TECHNOLOGY_PREVIEW_FIELDS = [
  {
    field: 'description',
    label: '',
  },
  {
    field: 'marketingRightAvailableSought',
    label: 'Marketing rights available / sought',
    format: formatTextList,
  },
  {
    field: 'companyName',
    label: 'Company name',
  },
  {
    field: 'differentiator',
    label: 'Partnering status',
  },
  {
    field: 'categorySector',
    label: 'Sector',
  },
  {
    field: 'categorySubSectors',
    label: 'Subsector(s)',
    format: formatTextList,
  },
]

export const SERVICES_PREVIEW_FIELDS = [
  {
    field: 'description',
    label: '',
  },
  {
    field: 'serviceAvailability',
    label: 'Service availability',
    format: formatTextList,
  },
  {
    field: 'companyName',
    label: 'Company name',
  },
  {
    field: 'differentiator',
    label: 'Partnering status',
  },
  {
    field: 'categorySector',
    label: 'Sector',
  },
  {
    field: 'categorySubSectors',
    label: 'Subsector(s)',
    format: formatTextList,
  },
]

export const BIO_PREVIEW_FIELDS = [
  {
    field: 'description',
    label: '',
  },
  {
    field: 'companyName',
    label: 'Company name',
  },
  {
    field: 'devPhase',
    label: 'Development phase',
    render: (profile: AnyProfile) => {
      const { devPhase } = profile as CompanyAsset
      const value = devPhase || 'N/A'

      return <LinearProgressWithLabel label={value} value={calcStageProgress(BIO_DEV_PHASE_ITEMS, value)} />
    },
  },
  {
    field: 'moleculeType',
    label: 'Molecule Type',
  },
  {
    field: 'differentiator',
    label: 'Partnering status',
  },
  {
    field: 'categorySector',
    label: 'Sector',
  },
  {
    field: 'categorySubSectors',
    label: 'Subsector(s)',
    format: formatTextList,
  },
]

export const PREVIEW_FIELDS_MAP_TO_PROFILES: Record<ProfileType, ResultProfileItemProps['fields']> = {
  [ProfileType.delegate]: DELEGATE_PREVIEW_FIELDS,
  [ProfileType.company]: COMPANY_PREVIEW_FIELDS,
  [ProfileType.biotech]: BIO_PREVIEW_FIELDS,
  [ProfileType.medtech]: MEDTECH_PREVIEW_FIELDS,
  [ProfileType.technologies]: TECHNOLOGY_PREVIEW_FIELDS,
  [ProfileType.services]: SERVICES_PREVIEW_FIELDS,
}

export const RECENT_LABELS = [
  {
    label: translationKeys.SEARCH_FILTER_BY_RECENT_WEEK,
    value: SearchFilterByRecent.SEARCH_FILTER_BY_RECENT_WEEK,
  },
  {
    label: translationKeys.SEARCH_FILTER_BY_RECENT_MONTH,
    value: SearchFilterByRecent.SEARCH_FILTER_BY_RECENT_MONTH,
  },
  {
    label: translationKeys.SEARCH_FILTER_BY_RECENT_THREE_MONTHS,
    value: SearchFilterByRecent.SEARCH_FILTER_BY_RECENT_THREE_MONTHS,
  },
]

export const META_FILTER_LABELS_MAP = {
  [MetaFilterTypes.BY_NEW_ITEMS]: translationKeys.NEW_ITEMS_LABEL,
  [MetaFilterTypes.BY_DATE_RANGE]: translationKeys.SEARCH_FILTER_BY_DATE_RANGE,
  [MetaFilterTypes.BY_EVENT]: translationKeys.SEARCH_FILTER_BY_EVENT,
  [MetaFilterTypes.BY_RECENT]: translationKeys.SEARCH_FILTER_BY_RECENT,
  [MetaFilterTypes.DEFAULT]: translationKeys.SEARCH_FILTER_BY_DATE,
}
